import { useEffect, useState } from 'react'

import { OrganizationVisibility } from '@ankor-io/common/types/account'
import { Button, Field, Input, Label, Select } from '@ankor-io/ui-components'

export type OrganizationsFilters = {
  organizationName: string
  organizationId: string
  visibility: string
  vesselName: string
}

type OrganizationsFiltersProps = {
  filters: OrganizationsFilters
  onFiltersChanged: (filters: OrganizationsFilters) => void
  isApplyingFilters: boolean
}

const OrganizationsFilters = ({ filters, onFiltersChanged, isApplyingFilters }: OrganizationsFiltersProps) => {
  const [updatedFilters, setUpdatedFilters] = useState<OrganizationsFilters>(filters)
  const [dateError, setDateError] = useState<string | null>(null)

  // Compare filters by value instead of reference
  const filtersUnchanged =
    filters.organizationName === updatedFilters.organizationName &&
    filters.organizationId === updatedFilters.organizationId &&
    filters.visibility === updatedFilters.visibility &&
    filters.vesselName === updatedFilters.vesselName

  // We need a useEffect to update the updatedFilters state when the filters prop changes since there is a "Clear filters" button outside of this component
  useEffect(() => {
    setUpdatedFilters(filters)
  }, [filters])

  const handleClearFilters = () => {
    const defaultFilters: OrganizationsFilters = {
      organizationName: '',
      organizationId: '',
      visibility: '',
      vesselName: '',
    }
    setUpdatedFilters(defaultFilters)
    setDateError(null)
    onFiltersChanged(defaultFilters)
  }

  return (
    <div className="flex items-center justify-between flex-col gap-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="w-full grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {/* Organization Name */}
        <Field>
          <Label>Organization Name</Label>
          <Input
            type="text"
            placeholder="Organization Name"
            aria-label="Organization Name"
            value={updatedFilters.organizationName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUpdatedFilters({ ...updatedFilters, organizationName: e.target.value })
            }
            disabled={isApplyingFilters}
          />
        </Field>

        {/* Organization ID */}
        <Field>
          <Label>Organization ID</Label>
          <Input
            type="text"
            placeholder="Organization ID"
            aria-label="Organization ID"
            value={updatedFilters.organizationId}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUpdatedFilters({ ...updatedFilters, organizationId: e.target.value })
            }
            disabled={isApplyingFilters}
          />
        </Field>

        {/* Visibility */}
        <Field>
          <Label>Visibility</Label>
          <Select
            value={updatedFilters.visibility}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setUpdatedFilters({ ...updatedFilters, visibility: e.target.value })
            }
            disabled={isApplyingFilters}
          >
            <option value="">All Visibilities</option>
            {Object.values(OrganizationVisibility).map((visibility) => (
              <option
                key={visibility}
                value={visibility}
              >
                {visibility}
              </option>
            ))}
          </Select>
        </Field>

        {/* Vessel Name */}
        <Field>
          <Label>Vessel Name</Label>
          <Input
            type="text"
            placeholder="Vessel Name"
            aria-label="Vessel Name"
            value={updatedFilters.vesselName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setUpdatedFilters({ ...updatedFilters, vesselName: e.target.value })
            }
            disabled={isApplyingFilters}
          />
        </Field>

        {/* Filter action button */}
        <div className="flex gap-x-2 self-end justify-end">
          <Button
            color="primary"
            className="w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={filtersUnchanged || dateError !== null}
            onClick={() => onFiltersChanged(updatedFilters)}
          >
            {isApplyingFilters ? 'Applying...' : 'Apply'}
          </Button>
          {!isApplyingFilters &&
            (filters.organizationName !== '' ||
              filters.organizationId !== '' ||
              filters.visibility !== '' ||
              filters.vesselName !== '') && (
              <Button
                plain
                className="w-full sm:w-auto"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            )}
        </div>
      </div>
    </div>
  )
}

export default OrganizationsFilters
